import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import i18n, { SUPPORTED, NON_EN, COUNTRY_LANG, STORAGE_KEY, DISMISS_KEY } from "../i18n";

const LangContext = createContext(null);

export const LangProvider = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const segment = location.pathname.split("/")[1];
  const lang = NON_EN.some((l) => l.code === segment) ? segment : "en";
  const [suggestion, setSuggestion] = useState(null);
  const [dismissed, setDismissed] = useState(() => !!localStorage.getItem(DISMISS_KEY));

  useEffect(() => {
    const meta = SUPPORTED.find((s) => s.code === lang) || SUPPORTED[0];
    if (i18n.language !== lang) i18n.changeLanguage(lang);
    document.documentElement.lang = lang;
    document.documentElement.dir = meta.dir;
    if (lang !== "en") localStorage.setItem(STORAGE_KEY, lang);
  }, [lang]);

  // Stored preference wins over everything: apply once on arrival.
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && stored !== "en" && NON_EN.some((l) => l.code === stored)) {
      const seg = window.location.pathname.split("/")[1];
      if (!NON_EN.some((l) => l.code === seg)) {
        const path = window.location.pathname === "/" ? "" : window.location.pathname;
        navigate(`/${stored}${path}${window.location.search}${window.location.hash}`, { replace: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Suggestion: browser language first, country as a supporting signal.
  useEffect(() => {
    if (lang !== "en" || dismissed || localStorage.getItem(STORAGE_KEY)) return;
    const browser = (navigator.language || "en").slice(0, 2).toLowerCase();
    if (NON_EN.some((l) => l.code === browser)) {
      setSuggestion(browser);
      return;
    }
    let cancelled = false;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/geo`)
      .then((r) => r.json())
      .then((d) => {
        if (cancelled) return;
        const code = COUNTRY_LANG[(d.country || "").toUpperCase()];
        if (code && NON_EN.some((l) => l.code === code)) setSuggestion(code);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [lang, dismissed]);

  const barePath = lang === "en" ? location.pathname : location.pathname.slice(3) || "/";

  const localize = useCallback(
    (to) => {
      if (lang === "en") return to;
      const m = to.match(/^([^?#]*)([?#].*)?$/);
      const path = m[1] === "/" ? "" : m[1];
      return `/${lang}${path}${m[2] || ""}`;
    },
    [lang]
  );

  const switchLang = useCallback(
    (code) => {
      localStorage.setItem(STORAGE_KEY, code);
      const rest = `${location.search}${location.hash}`;
      const path = barePath === "/" ? "" : barePath;
      navigate(code === "en" ? `${path || "/"}${rest}` : `/${code}${path}${rest}`);
      setSuggestion(null);
      setDismissed(true);
      localStorage.setItem(DISMISS_KEY, "1");
    },
    [barePath, location.search, location.hash, navigate]
  );

  const dismissSuggestion = useCallback(() => {
    setDismissed(true);
    localStorage.setItem(DISMISS_KEY, "1");
    setSuggestion(null);
  }, []);

  return (
    <LangContext.Provider
      value={{ lang, dir: (SUPPORTED.find((s) => s.code === lang) || SUPPORTED[0]).dir, suggestion, localize, switchLang, dismissSuggestion }}
    >
      {children}
    </LangContext.Provider>
  );
};

export const useLang = () => useContext(LangContext);

// Localised SEO: title, meta description, canonical + hreflang per page.
export const usePageSeo = (pageKey) => {
  const { t } = useTranslation();
  const { lang } = useLang();
  const location = useLocation();
  useEffect(() => {
    document.title = t(`seo.${pageKey}.title`);
    const desc = t(`seo.${pageKey}.desc`);
    let meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", desc);

    const origin = window.location.origin;
    const seg = location.pathname.split("/")[1];
    const bare = NON_EN.some((l) => l.code === seg) ? location.pathname.slice(3) || "/" : location.pathname;

    document.querySelectorAll("link[data-seo-lang]").forEach((n) => n.remove());
    const head = document.head;
    const add = (attrs) => {
      const link = document.createElement("link");
      Object.entries(attrs).forEach(([k, v]) => link.setAttribute(k, v));
      link.setAttribute("data-seo-lang", "1");
      head.appendChild(link);
    };
    add({ rel: "canonical", href: `${origin}${lang === "en" ? bare : `/${lang}${bare === "/" ? "" : bare}`}` });
    SUPPORTED.forEach((l) => {
      add({
        rel: "alternate",
        hreflang: l.code,
        href: `${origin}${l.code === "en" ? bare : `/${l.code}${bare === "/" ? "" : bare}`}`,
      });
    });
    add({ rel: "alternate", hreflang: "x-default", href: `${origin}${bare}` });
  }, [pageKey, lang, location.pathname, t]);
};
